import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

import Button from './Button';
import Modal from './Modal';
import { V2ApiError } from '../services/client';
import { applyValidatedCouponResponse, cartSkusForCouponValidation } from '../services/commerce';
import {
  filterCouponsForStore,
  listCouponsCccFormat,
  validateCartCoupon,
} from '../services/coupons';
import { useV2Session } from '../state/SessionProvider';
import { paths } from '../config/paths';

function couponListDiscountLabel(coupon) {
  if (coupon.discount_type === 'percentage') {
    const pct = coupon.discount ? `${coupon.discount}% off` : '';
    const max = coupon.maxDiscount ? ` (max ₹${coupon.maxDiscount})` : '';
    if (pct) return `${pct}${max}`;
    if (coupon.maxDiscount) return `Up to ₹${coupon.maxDiscount} off`;
    return 'Percentage discount';
  }
  if (coupon.discount_type === 'free_sku') {
    return 'Free item';
  }
  if (coupon.couponValue) return `₹${coupon.couponValue} off`;
  if (typeof coupon.discount === 'number') return `₹${coupon.discount} off`;
  if (coupon.discount) return String(coupon.discount);
  if (coupon.couponTitle) return coupon.couponTitle;
  return '—';
}

const NO_STORE_MSG =
  'Select a delivery address first so we can verify coupons for your store.';

export default function CheckoutCouponModal({
  open,
  onClose,
  products,
  cartSubtotal,
  storeId,
  onApplied,
}) {
  const { mobile, loggedIn } = useV2Session();
  const navigate = useNavigate();
  const [listLoading, setListLoading] = useState(false);
  const [coupons, setCoupons] = useState([]);
  const [listError, setListError] = useState('');
  const [manualCode, setManualCode] = useState('');
  const [applying, setApplying] = useState(null);
  const [formError, setFormError] = useState('');

  const skus = useMemo(() => cartSkusForCouponValidation(products), [products]);
  const subtotal = Math.max(0, Number(cartSubtotal) || 0);
  const resolvedStoreId = storeId != null && String(storeId).trim() !== '' ? String(storeId).trim() : null;

  useEffect(() => {
    if (!open) return undefined;
    setFormError('');
    setManualCode('');
    let cancelled = false;
    (async () => {
      setListLoading(true);
      setListError('');
      try {
        const rows = resolvedStoreId
          ? await listCouponsCccFormat({ storeId: resolvedStoreId })
          : await listCouponsCccFormat();
        const filtered = resolvedStoreId
          ? rows
          : filterCouponsForStore(rows, null);
        if (!cancelled) setCoupons(filtered);
      } catch (e) {
        if (!cancelled) {
          const msg = e instanceof V2ApiError ? e.message : 'Could not load coupons.';
          setListError(msg);
          setCoupons([]);
        }
      } finally {
        if (!cancelled) setListLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, resolvedStoreId]);

  const applyCode = useCallback(
    async (rawCode) => {
      const c = String(rawCode || '').trim().toUpperCase();
      if (!c) {
        setFormError('Enter a coupon code.');
        return;
      }
      if (!loggedIn || !mobile) {
        setFormError('Sign in to use coupons.');
        return;
      }
      if (!resolvedStoreId) {
        setFormError(NO_STORE_MSG);
        return;
      }
      setFormError('');
      setApplying(c);
      try {
        const resp = await validateCartCoupon({
          mobile,
          code: c,
          skus,
          cartTotal: subtotal,
          storeId: resolvedStoreId,
        });
        await applyValidatedCouponResponse(resp);
        onApplied?.();
        onClose?.();
      } catch (e) {
        const msg = e instanceof V2ApiError ? e.message : 'Could not apply coupon.';
        setFormError(msg);
      } finally {
        setApplying(null);
      }
    },
    [loggedIn, mobile, onApplied, onClose, resolvedStoreId, skus, subtotal]
  );

  const visibleCoupons = coupons.filter((c) => !c.hideCoupon);

  return (
    <Modal open={open} onClose={onClose} title="Coupons">
      <Stack spacing={2}>
        {!loggedIn ? (
          <Alert severity="info">
            Sign in to apply coupons.
            <Box sx={{ mt: 1 }}>
              <Button size="small" variant="outlined" onClick={() => navigate(paths.auth)}>
                Sign in
              </Button>
            </Box>
          </Alert>
        ) : null}

        {loggedIn && !resolvedStoreId ? (
          <Alert severity="warning">{NO_STORE_MSG}</Alert>
        ) : null}

        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Have a code?
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Coupon code"
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value)}
              disabled={applying != null}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  applyCode(manualCode);
                }
              }}
            />
            <Button
              variant="contained"
              disabled={applying != null}
              onClick={() => applyCode(manualCode)}
              sx={{ flexShrink: 0, minWidth: { sm: 100 } }}
            >
              {applying === manualCode.trim().toUpperCase() && manualCode.trim() ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                'Apply'
              )}
            </Button>
          </Stack>
        </Box>

        {formError ? <Alert severity="error">{formError}</Alert> : null}
        {listError ? <Alert severity="warning">{listError}</Alert> : null}

        <Divider />
        <Typography variant="subtitle2">Available coupons</Typography>
        {listLoading ? (
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress size={28} />
          </Box>
        ) : visibleCoupons.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No coupons right now.
          </Typography>
        ) : (
          <Stack spacing={1.5} sx={{ maxHeight: 360, overflow: 'auto', pr: 0.5 }}>
            {visibleCoupons.map((coupon, index) => {
              const code = String(coupon.couponCode || '').trim();
              const upper = code.toUpperCase();
              const busy = applying === upper;
              const key = `${coupon.iD ?? ''}-${code}-${index}`;
              const minOrder = coupon.minimumPurchaseAmount;
              const titleShown =
                Boolean(coupon.couponTitle) && coupon.couponTitle !== coupon.couponCode;
              const discountLabel = couponListDiscountLabel(coupon);
              const showDiscountLabel =
                discountLabel &&
                !(coupon.discount_type === 'free_sku' && titleShown);
              return (
                <Box
                  key={key}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 1.5,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: 1,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle2" fontWeight={700}>
                      {coupon.couponCode}
                    </Typography>
                    {titleShown ? (
                      <Typography variant="body2" color="text.secondary" display="block">
                        {coupon.couponTitle}
                      </Typography>
                    ) : null}
                    {showDiscountLabel ? (
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {discountLabel}
                      </Typography>
                    ) : null}
                    {minOrder ? (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        Minimum order ₹{minOrder}
                      </Typography>
                    ) : null}
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={applying != null}
                    onClick={() => applyCode(coupon.couponCode)}
                    sx={{ flexShrink: 0 }}
                  >
                    {busy ? <CircularProgress size={18} /> : 'Apply'}
                  </Button>
                </Box>
              );
            })}
          </Stack>
        )}
      </Stack>
    </Modal>
  );
}
